import React from 'react';
import { Button, Space, Typography, type App } from 'antd';
import type { TFunction } from 'i18next';
import type { WorkflowInvoiceLine } from '../../../types/workflowInvoice';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { quantityGap } from '../workflowInvoiceQuantity';

type ModalApi = ReturnType<typeof App.useApp>['modal'];
export type QuantityGapChoice = 'entered' | 'dimensions';

/**
 * FZ.2 (owner 2026-10-30): before saving, lines whose amount to pay differs from
 * their whole pieces by more than the invoice's print rounding are shown with
 * the amount from the dimensions; the operator saves either amount or goes back.
 * Resolves null when the pop-up is closed.
 */
export function confirmQuantityGaps(
  modal: ModalApi,
  t: TFunction,
  lines: Array<{ position: number; line: WorkflowInvoiceLine }>,
): Promise<QuantityGapChoice | null> {
  const amount = (value: number, unit: string) => `${formatLocalizedQuantity(value, 'pl')} ${formatUnit(unit)}`;
  return new Promise((resolve) => {
    let settled = false;
    const finish = (choice: QuantityGapChoice | null) => {
      if (settled) return;
      settled = true;
      instance.destroy();
      resolve(choice);
    };
    const instance = modal.confirm({
      title: t('invoices:workflow.quantityGap.title'),
      width: 560,
      closable: true,
      onCancel: () => finish(null),
      content: (
        <Space direction="vertical" size={8} data-testid="invoice-quantity-gap-confirm">
          <Typography.Text>{t('invoices:workflow.quantityGap.intro')}</Typography.Text>
          {lines.map(({ position, line }) => {
            const gap = quantityGap(line)!;
            return (
              <Typography.Text key={line.key} data-testid={`invoice-quantity-gap-${line.key}`}>
                {t('invoices:workflow.quantityGap.line', {
                  position,
                  product: line.productName ?? '',
                  entered: amount(gap.printed, gap.unit),
                  pieces: amount(gap.pieces, 'pcs'),
                  physical: amount(gap.physical, gap.unit),
                })}
              </Typography.Text>
            );
          })}
          <Typography.Text type="secondary">{t('invoices:workflow.quantityGap.hint')}</Typography.Text>
        </Space>
      ),
      footer: (
        <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }} wrap>
          <Button onClick={() => finish(null)}>{t('common:cancel')}</Button>
          <Button onClick={() => finish('dimensions')} data-testid="invoice-quantity-gap-dimensions">
            {t('invoices:workflow.quantityGap.saveDimensions')}
          </Button>
          <Button type="primary" onClick={() => finish('entered')} data-testid="invoice-quantity-gap-entered">
            {t('invoices:workflow.quantityGap.saveEntered')}
          </Button>
        </Space>
      ),
    });
  });
}
